// =============================================================================
// Workspace Storage
// Canonical storage API for the workspace
// =============================================================================

import { Workspace, WorkspaceExportEnvelope, WorkspaceImportPayload, WorkspaceSnapshot } from '@shared/types';
import {
  loadRawFromLocalStorage,
  saveRawToLocalStorage,
  clearLocalStorage,
  workspaceExists,
} from './localStorageAdapter';

// Current schema version
const CURRENT_SCHEMA = 2;
const WORKSPACE_VERSION = '1.0.0';
const EXPORT_KIND = 'indxone-workspace-export';
const SNAPSHOT_VERSION = '1.0.0';

/**
 * Default empty workspace
 */
export function createDefaultWorkspace(): Workspace {
  const now = Date.now();
  return {
    meta: {
      schema: CURRENT_SCHEMA,
      version: WORKSPACE_VERSION,
      createdAt: now,
      updatedAt: now,
      migratedFrom: [],
      migrationRecords: [],
    },
    activeMissionId: null,
    profile: {
      id: 'profile-default',
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      role: '',
      mode: 'expert',
      organizationType: 'freelance',
      address: '',
      postalCode: '',
      city: '',
      country: '',
      siret: '',
      vatNumber: '',
      iban: '',
      rib: '',
      bankName: '',
      logoDataUrl: '',
      status: 'sasu',
      netTarget: 2500,
      tjm: 650,
      daysPerMonth: 18,
      createdAt: now,
      updatedAt: now,
    },
    variables: {},
    notes: [],
    timeline: [],
    projects: [],
    evaluations: [],
    sipocs: [],
    moscows: [],
    racis: [],
    dealRooms: [],
    cras: [],
    treasuries: [],
    canvases: [],
    calcMissionSessions: [],
    estimations: [],
    preferences: {
      theme: 'dark',
      language: 'fr',
    },
  };
}

/**
 * Validate that a workspace has the correct schema
 */
function validateWorkspace(workspace: unknown): workspace is Workspace {
  if (!workspace || typeof workspace !== 'object') return false;
  
  const w = workspace as Partial<Workspace>;
  
  // Check required fields
  if (!w.meta || typeof w.meta.schema !== 'number') return false;
  if (w.profile && typeof w.profile !== 'object') return false;
  if (!Array.isArray(w.projects)) return false;
  if (!Array.isArray(w.evaluations)) return false;
  if (!Array.isArray(w.sipocs)) return false;
  if (!Array.isArray(w.moscows)) return false;
  if (!Array.isArray(w.racis)) return false;
  if (!Array.isArray(w.dealRooms)) return false;
  if (!Array.isArray(w.cras)) return false;
  if (!Array.isArray(w.treasuries)) return false;
  if (w.calcMissionSessions && !Array.isArray(w.calcMissionSessions)) return false;
  if (w.variables && typeof w.variables !== 'object') return false;
  if (w.notes && !Array.isArray(w.notes)) return false;
  if (w.timeline && !Array.isArray(w.timeline)) return false;
  if (w.canvases && !Array.isArray(w.canvases)) return false;
  
  return true;
}

function isWorkspaceExportEnvelope(value: unknown): value is WorkspaceExportEnvelope {
  if (!value || typeof value !== 'object') return false;
  const envelope = value as Partial<WorkspaceExportEnvelope>;

  return (
    envelope.kind === EXPORT_KIND &&
    envelope.snapshotVersion === SNAPSHOT_VERSION &&
    typeof envelope.exportedAt === 'number' &&
    typeof envelope.appVersion === 'string' &&
    typeof envelope.schema === 'number' &&
    validateWorkspace(envelope.workspace)
  );
}

export function isValidWorkspaceSnapshot(workspace: unknown): workspace is WorkspaceImportPayload {
  return validateWorkspace(workspace) || isWorkspaceExportEnvelope(workspace);
}

/**
 * Migrate a workspace to the current schema
 * Currently just ensures schema is set correctly
 */
function migrateWorkspace(workspace: Workspace): Workspace {
  const base = createDefaultWorkspace();

  const normalized: Workspace = {
    ...base,
    ...workspace,
    profile: {
      ...base.profile,
      ...(workspace.profile || {}),
      updatedAt: workspace.profile?.updatedAt || workspace.meta.updatedAt || Date.now(),
    },
    calcMissionSessions: Array.isArray(workspace.calcMissionSessions) ? workspace.calcMissionSessions : [],
    estimations: Array.isArray((workspace as Partial<Workspace>).estimations) ? (workspace as Partial<Workspace>).estimations! : [],
    canvases: Array.isArray((workspace as Partial<Workspace>).canvases) ? (workspace as Partial<Workspace>).canvases! : [],
    projects: Array.isArray(workspace.projects)
      ? workspace.projects.map((project) => (
        (project.missionStatus as string | undefined) === 'scoping'
          ? { ...project, missionStatus: 'cadrage' as const, updatedAt: Date.now() }
          : project
      ))
      : [],
    meta: {
      ...base.meta,
      ...workspace.meta,
      schema: CURRENT_SCHEMA,
      updatedAt: Date.now(),
    },
  };

  // Migration: backfill canvasId, proposalId, quoteId, memoryId on projects
  const canvasByProjectId = new Map(normalized.canvases.map((canvas) => [canvas.projectId, canvas.id] as const));
  for (const project of normalized.projects) {
    const canvasId = canvasByProjectId.get(project.id);
    if (canvasId && !project.canvasId) {
      project.canvasId = canvasId;
    }
  }

  return normalized;
}

/**
 * Load workspace from localStorage
 * Returns default workspace if none exists
 */
export function loadWorkspace(): Workspace {
  const raw = loadRawFromLocalStorage<WorkspaceSnapshot>();
  
  if (!raw) {
    return createDefaultWorkspace();
  }
  
  // Validate and migrate
  if (!validateWorkspace(raw)) {
    console.warn('[Storage] Invalid workspace format, creating default');
    return createDefaultWorkspace();
  }
  
  const migrated = migrateWorkspace(raw);
  return migrated;
}

/**
 * Save workspace to localStorage
 * Validates before saving
 */
export function saveWorkspace(workspace: Workspace): boolean {
  // Update timestamp
  const updatedWorkspace = {
    ...workspace,
    meta: {
      ...workspace.meta,
      updatedAt: Date.now(),
    },
  };
  
  // Validate before saving
  if (!validateWorkspace(updatedWorkspace)) {
    console.error('[Storage] Invalid workspace, refusing to save');
    return false;
  }
  
  return saveRawToLocalStorage(updatedWorkspace);
}

/**
 * Parse a workspace snapshot from a JSON string.
 * Returns null when parsing or validation fails.
 */
export function parseWorkspaceSnapshot(raw: string): Workspace | null {
  try {
    const parsed = JSON.parse(raw) as WorkspaceImportPayload;
    if (isWorkspaceExportEnvelope(parsed)) {
      return migrateWorkspace(parsed.workspace);
    }
    if (!validateWorkspace(parsed)) {
      return null;
    }
    return migrateWorkspace(parsed);
  } catch {
    return null;
  }
}

/**
 * Reset workspace to default (clears all data)
 */
export function resetWorkspace(): Workspace {
  clearLocalStorage();
  return createDefaultWorkspace();
}

/**
 * Check if a workspace has been saved before
 */
export function hasExistingWorkspace(): boolean {
  return workspaceExists();
}

/**
 * Take a snapshot of the current workspace for export
 */
export function takeWorkspaceSnapshot(): WorkspaceExportEnvelope {
  const workspace = loadWorkspace();

  return {
    kind: EXPORT_KIND,
    snapshotVersion: SNAPSHOT_VERSION,
    exportedAt: Date.now(),
    appVersion: WORKSPACE_VERSION,
    schema: workspace.meta.schema,
    workspace,
  };
}
